import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  DesignTestInputDto,
  DesignTestOutputDto
} from '../../../models/job-request-iterations-dto.interface';

interface RetortReportValues {
  solidsPercent?: number;
  nafPercent?: number;
  waterPercent?: number;
  nafWaterRatio?: string;
  qaPercent?: number;
}

@Component({
  selector: 'app-retort-test-report',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './retort-test-report.component.html',
  styleUrl: './retort-test-report.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RetortTestReportComponent {
  @Input({ required: true }) test!: DesignTestInputDto;
  @Input({ required: true }) output!: DesignTestOutputDto;
  @Input() mudSpecificGravity: number | null = null;
  @Input() baseFluidSpecificGravity: number | null = null;

  protected retortReportValues(): RetortReportValues | null {
    const cellMudWeight = this.numberValue(['cellmudweight', 'cellwithmudweight']);
    const cellTareWeight = this.numberValue(['celltareweight', 'cellweight']);
    const cellAfterWeight = this.numberValue(['cellafterweight']);
    const totalVolume = this.numberValue(['totalvolume', 'samplevolumeml']);
    const waterVolume = this.numberValue(['watervolume']);
    const mudSpecificGravity = this.mudSpecificGravity
      ?? this.numberValue(['mudspecificgravity', 'measuredspecificgravity', 'specificgravity']);
    const baseFluidSpecificGravity = this.baseFluidSpecificGravity
      ?? this.numberValue(['basefluidspecificgravity']);

    const sampleWeight = this.subtract(cellMudWeight, cellTareWeight);
    const solidsWeight = this.subtract(cellAfterWeight, cellTareWeight);
    const nafVolume = totalVolume !== undefined && waterVolume !== undefined
      ? Math.max(totalVolume - waterVolume, 0)
      : undefined;
    const mudVolume = sampleWeight !== undefined && mudSpecificGravity !== undefined && mudSpecificGravity > 0
      ? sampleWeight / mudSpecificGravity
      : undefined;
    const nafPercent = this.numberValue(['retortnafoftotal', 'nafoftotal', 'nafpercent', 'nafvolpercent'])
      ?? this.percent(nafVolume, mudVolume);
    const waterPercent = this.numberValue(['retortwateroftotal', 'wateroftotal', 'waterpercent', 'watervolpercent'])
      ?? this.percent(waterVolume, mudVolume);
    const solidsPercent = this.numberValue(['retortsolidsvolumepercent', 'solidsvolumepercent', 'solidspercent'])
      ?? (nafPercent !== undefined && waterPercent !== undefined
        ? 100 - nafPercent - waterPercent
        : undefined);
    const nafOfLiquid = this.percent(nafVolume, this.add(nafVolume, waterVolume));
    const waterOfLiquid = this.percent(waterVolume, this.add(nafVolume, waterVolume));
    const nafWaterRatio = this.value([
      'retortnafwaterratio',
      'nafwaterratio',
      'nafwaterratio'
    ]) ?? (nafOfLiquid !== undefined && waterOfLiquid !== undefined
      ? `${Math.round(nafOfLiquid)}/${Math.round(waterOfLiquid)}`
      : undefined);
    const qaPercent = this.numberValue(['retorterrormargin', 'retortqa', 'qapercent', 'qa'])
      ?? (sampleWeight !== undefined
        && sampleWeight > 0
        && solidsWeight !== undefined
        && waterVolume !== undefined
        && nafVolume !== undefined
        ? 100 * (
            solidsWeight
            + waterVolume
            + nafVolume * (baseFluidSpecificGravity ?? 0)
          ) / sampleWeight
        : undefined);

    if ([solidsPercent, nafPercent, waterPercent, qaPercent].every(value => value === undefined)
      && nafWaterRatio === undefined) {
      return null;
    }

    return { solidsPercent, nafPercent, waterPercent, nafWaterRatio, qaPercent };
  }

  protected formatPercent(value: number | undefined): string {
    return value === undefined ? '—' : value.toFixed(1);
  }

  protected formatQa(value: number | undefined): string {
    return value === undefined ? '—' : `${value.toFixed(3)}%`;
  }

  private value(names: string[]): string | null {
    const acceptedNames = new Set(names);
    const parsedField = [...this.output.parsedValues, ...this.test.parsedInputValues].find(field =>
      acceptedNames.has(this.normalize(field.label)) && field.value?.trim());
    return parsedField?.value?.trim()
      || this.xmlValue(this.output.content, acceptedNames)
      || this.xmlValue(this.test.content, acceptedNames);
  }

  private numberValue(names: string[]): number | undefined {
    return this.toNumber(this.value(names));
  }

  private xmlValue(content: string | null, names: Set<string>): string | null {
    if (!content) return null;

    const document = new DOMParser().parseFromString(content, 'application/xml');
    if (document.querySelector('parsererror')) return null;

    for (const element of Array.from(document.getElementsByTagName('*'))) {
      if (element.localName.toLowerCase() === 'parameter') {
        if (!names.has(this.normalize(element.getAttribute('key')))) continue;
      } else if (!names.has(this.normalize(element.localName))) {
        continue;
      }

      if (element.children.length === 0) {
        const value = element.textContent?.trim();
        if (value) return value;
      }
    }

    return null;
  }

  private subtract(left: number | undefined, right: number | undefined): number | undefined {
    return left !== undefined && right !== undefined ? left - right : undefined;
  }

  private add(left: number | undefined, right: number | undefined): number | undefined {
    return left !== undefined && right !== undefined ? left + right : undefined;
  }

  private percent(numerator: number | undefined, denominator: number | undefined): number | undefined {
    return numerator !== undefined && denominator !== undefined && denominator > 0
      ? 100 * numerator / denominator
      : undefined;
  }

  private normalize(value: string | null | undefined): string {
    return (value ?? '').replace(/[\s_\-/,().:%]+/g, '').toLowerCase();
  }

  private toNumber(value: string | null): number | undefined {
    if (!value) return undefined;
    const parsed = Number(value.replace('%', '').trim());
    return Number.isFinite(parsed) ? parsed : undefined;
  }
}